<template>
  <div :class="['search', { 'search-open': displayDropdown }]">
    <label for="search-input" class="u-visually-hidden">{{ $t('search.label') }}</label>
    <input
      id="search-input"
      ref="input"
      @focus="onFocus"
      @keydown="onKeydown"
      @input="onInput"
      :name="new Date()"
      :value="$store.state.search.terms"
      :placeholder="placeholder"
      :disabled="disabled"
      class="search-input"
      type="text"
    >
    <button v-if="disabled" @click="onModify" class="search-modify">{{ $t('search.modify') }}</button>
    <span v-else class="search-submit">
      <sprite :type="'search'" :width="24" :height="24" />
    </span>
    <!-- Dropdown -->
    <div v-if="displayDropdown" class="search-dropdown">
      <!-- Results -->
      <div v-if="results.length > 0 && !loader" class="search-results">
        <case
          ref="results"
          v-for="result in results"
          @onKeydown="onCaseKeydown"
          @onClick="resultClick($event, result)"
          :key="result.slug"
          :lawCase="result"
        />
      </div>

      <!-- No results -->
      <div v-if="noResult && !loader" class="search-noresult">
        <h2 v-html="$t('search.noresult')" class="search-noresult-title" />
        <p class="search-noresult-text">{{ $store.state.search.terms }}</p>

        <a
          ref="noResultLink"
          v-html="$t('search.help')"
          @keydown="onNoResultKeydown"
          target="_blank"
          :href="`mailto:${$store.state.lawyer.item.email}`"
          class="button button-ghost"
        />
      </div>

      <!-- Loader -->
      <loader v-if="loader" />
    </div>
  </div>
</template>

<script>
// Dependencies
import _ from 'lodash';
// Components
import Sprite from '@/components/components/Sprite.vue';
import Loader from '@/components/components/Loader';
import Case from '@/components/modules/Case';

export default {
  components: {
    Sprite,
    Loader,
    Case
  },
  props: {
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      focus: false,
      cancelBlur: false,
      caseFocused: null,
      docClick: this.onDocumentClick.bind(this),
      debounceRequest: _.debounce(this.request.bind(this), 300)
    }
  },
  computed: {
    search() {
      return this.$store.state.search;
    },
    placeholder() {
       try {
        const { innerWidth } = window;
        if (innerWidth <= 550) return this.$t('search.placeholder-mobile');
        return this.$t('search.placeholder')
      } catch (error) {
        return this.$t('search.placeholder')
      }
    },
    results() {
      const results = this.$store.state.search.results.filter((result, index) => index < 12);
      return results;
    },
    noResult() {
      return this.$store.state.search.noResult && this.$store.state.search.terms.length > 4;
    },
    loader() {
      return this.$store.state.search.requests > 0;
    },
    displayDropdown() {
      if (!this.focus || this.$store.state.search.terms.length < 3 || this.disabled) {
        return false;
      }

      return this.$store.state.search.results.length > 0 || this.$store.state.search.requests > 0 || this.$store.state.search.noResult;
    }
  },
  mounted() {
    document.addEventListener('click', this.docClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.docClick);
  },
  methods: {
    resultClick(e, result) {
      this.$store.commit('search/updateTerms', result.content);
      this.$emit('resultClick', { e, lawCase: result });
    },
    request() {
      this.$store.dispatch('search/search');
    },
    next() {
      // If last one
      if (this.caseFocused === this.$refs.results.length - 1) {
        this.caseFocused = null;
        this.$refs.input.focus();
        return;
      }

      if (this.caseFocused === null) {
        this.caseFocused = 0;
      } else {
        this.caseFocused = this.caseFocused + 1;
      }

      this.launchFocus();
    },
    prev() {
      // If first one
      if (this.caseFocused === 0) {
        this.caseFocused = null;
        this.$refs.input.focus();
        return;
      }

      if (this.caseFocused === null) {
        this.caseFocused = this.$refs.results.length - 1;
      } else {
        this.caseFocused = this.caseFocused - 1;
      }

      this.launchFocus();
    },
    launchFocus() {
      const $results = this.$refs.results;
      $results[this.caseFocused].$el.focus();
    },
    toggleHelp() {
      this.$store.commit('help/toggleVisibility');
    },
    // Events
    onFocus() {
      this.caseFocused = null;
      this.focus = true;
    },
    onModify() {
      this.$emit('toggleModify');

      setTimeout(() => {
        this.$refs.input.focus();
      }, 150);
    },
    onKeydown(e) {
      const $results = this.$refs.results;
      const $noResultLink = this.$refs.noResultLink;

      // Tab
      if (e.keyCode === 9) {
        if (!e.shiftKey) {
          if ($results && $results.length > 0) {
            e.preventDefault();
            this.cancelBlur = true;
            this.next();
          }

          if ($noResultLink) {
            e.preventDefault();
            this.cancelBlur = true;
            $noResultLink.focus();
          }
        } else {
          this.focus = false;
        }
      }

      // Up / Down
      if (e.keyCode === 38 || e.keyCode === 40) {
        e.preventDefault();

        if ($results && $results.length > 0) {
          this.cancelBlur = true;

          // Up
          if (e.keyCode === 38) {
            this.prev();
            return;
          }

          this.next();
        }
      }
    },
    onCaseKeydown(e) {
      // Tab
      if (e.keyCode === 9) {
        if (e.shiftKey) {
          e.preventDefault();
          this.cancelBlur = true;
          this.prev();
          return;
        }

        if (this.caseFocused === this.$refs.results.length - 1) {
          this.focus = false;
          return;
        }

        e.preventDefault();
        this.cancelBlur = true;
        this.next();
      }

      // Up / Down
      if (e.keyCode === 38 || e.keyCode === 40) {
        e.preventDefault();
        this.cancelBlur = true;

        if (e.keyCode === 38) {
          this.prev();
          return;
        }

        this.next();
      }
    },
    onNoResultKeydown(e) {
      if (!e.shiftKey) {
        this.focus = false;
      }
    },
    onInput(e) {
      const { value } = e.currentTarget;
      this.$store.commit('search/updateTerms', value);

      // Remove results
      if (value.length < 3) {
        if (this.$store.state.search.results) {
          this.$store.commit('search/updateResults', []);
        }

        if (this.$store.state.search.noResult) {
          this.$store.commit('search/updateNoResult', false);
        }
      }

      // Launch request
      if (this.search.terms.length >= 3 && this.search.terms !== this.search.prevTerms) {
        this.debounceRequest(e);
      }
    },
    onDocumentClick(e) {
      const { target } = e;

      if (!this.$el.contains(target)) {
        this.focus = false;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .search {
    position: relative;
    background-color: color('background');

    &.search-open {
      box-shadow: 0px 2px 8px rgba(20, 20, 20, .16);
      border-radius: var(--symplicy-border-radius);

      .search-input {
        background-color: color('background');
        border-radius: var(--symplicy-border-radius) var(--symplicy-border-radius) 0 0;
      }
    }

    &:not(.search-open) {
      .search-input {
        &:focus {
          background-color: color('background');
          box-shadow: 0px 2px 8px rgba(20, 20, 20, .16);
        }
      }
    }

    &-input {
      @include padding(4px 50px 4px 16px);
      display: block;
      width: 100%;
      transition: background-color .3s $ease-out-quart, box-shadow .3s $ease-out-quart;
      border: 0;
      border-radius: var(--symplicy-border-radius);
      background-color: color('input-background');
      appearance: none;
      color: color('input-text');
      font-family: var(--symplicy-font-secondary);
      font-size: rem(16px);
      line-height: (24 / 14);
      outline: none;

      @include mq($until: 'm') {
        @include padding(null 40px null 10px);
      }

      &:disabled {
        @include padding(3px null);
        @include padding(null 80px null null);

        @include mq($until: 'm') {
          @include padding(null 67px null null);
        }
      }

      &::placeholder {
        color: color('text', .6);
      }
    }

    &-submit {
      @include position(absolute, 0 0 null null);
      @include padding(4px 16px);
      cursor: pointer;

      .svg {
        display: block;
        fill: color('highlight');
      }
    }

    &-modify {
      @include position(absolute, 0 0 null null);
      @include padding(9px 16px);
      display: block;
      flex-shrink: 0;
      color: color('highlight');
      font-family: var(--symplicy-font-secondary);
      font-size: rem(12px);
      font-weight: 600;
      line-height: 1.2;
      cursor: pointer;
      outline: none;

      @include mq($until: 'm') {
        @include padding(7px 10px);
      }
    }

    // Dropdown
    // -----------------------------------------------------------------------------
    &-dropdown {
      @include size(100%, auto);
      border-radius: 0 0 calc(var(--symplicy-border-radius) / 2) calc(var(--symplicy-border-radius) / 2);
      border-top: 1px solid color('border');
      overflow: auto;

      // Results
      .case {
        &:not(:first-child) {
          border-top: 1px solid color(border);
        }

        &:last-child {
          border-radius: 0 0 var(--symplicy-border-radius) var(--symplicy-border-radius);
        }
      }

      // No result
      .search-noresult {
        @include padding(16px null);
        width: 100%;
        text-align: center;

        &-title {
          color: color('text-light');
          font-family: var(--symplicy-font-secondary);
          font-size: rem(14px);
          line-height: (16 / 14);
        }

        &-text {
          @include margin(4px null null);
          color: color('text');
          font-family: var(--symplicy-font-secondary);
          font-size: rem(14px);
          font-weight: 600;
          line-height: (16 / 14);
        }

        .button {
          @include margin(16px null null);
          font-size: rem(14px);
        }
      }

      // Loader
      .loader {
        @include margin(20px null);
      }
    }
  }
</style>
